import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  arrow,
} from '@floating-ui/react-dom'
import { InfoIcon } from '@gocanvas/gcds/icons'

const getArrowStyles = (arrowData, tooltipPosition) => {
  const { x: arrowX, y: arrowY, arrowHeight, arrowWidth } = arrowData

  if (arrowX == null && arrowY == null) return {}

  const isTopOrBottom = ['top', 'bottom'].includes(tooltipPosition)
  const dynamicAxis = isTopOrBottom ? 'left' : 'top'
  const dynamicAxisPosition = isTopOrBottom ? arrowX : arrowY
  const arrowSize = isTopOrBottom ? arrowHeight : arrowWidth
  const staticAxis = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[tooltipPosition]

  return {
    position: 'absolute',
    '--gcds-tooltip--arrow-transform': 'initial',
    [`--gcds-tooltip--arrow-position__${staticAxis}`]: `-${arrowSize}px`,
    [`--gcds-tooltip--arrow-position__${dynamicAxis}`]: `${dynamicAxisPosition}px`,
  }
}

export const IconTooltip = ({
  tooltipContent,
  width, // width can be omitted to have the tooltip auto-size based on content
  position = 'bottom',
  allowedPositions = ['top', 'bottom', 'left', 'right'],
  Icon = InfoIcon,
  delay = 0.1,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [currentPosition, setCurrentPosition] = useState(position)
  const arrowRef = useRef(null)
  const delayTimeout = useRef(null)
  const headerHeight =
    parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        '--header-height'
      )
    ) || 60
  const placementPadding = 8
  const arrowHeight = arrowRef.current?.offsetHeight || 12
  const arrowWidth = arrowRef.current?.offsetWidth || 20
  const arrowOffset = ['top', 'bottom'].includes(currentPosition)
    ? arrowHeight
    : arrowWidth

  const { refs, floatingStyles, placement, middlewareData } = useFloating({
    placement: position,
    middleware: [
      offset(arrowOffset + 4),
      flip({
        boundary: 'viewport',
        fallbackPlacements: allowedPositions.filter(p => p !== position),
        padding: {
          top: headerHeight + placementPadding,
          bottom: placementPadding,
          left: placementPadding,
          right: placementPadding,
        },
      }),
      arrow({ element: arrowRef.current }),
    ],
    whileElementsMounted: autoUpdate,
  })

  const {} = middlewareData.arrow || {}

  useEffect(() => {
    if (placement) {
      const newPosition = placement.split('-')[0]
      setCurrentPosition(newPosition)
    }
  }, [placement])

  useEffect(() => {
    const handleEscape = e => {
      if (e.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  useEffect(() => {
    return () => clearTimeout(delayTimeout.current)
  }, [])

  const showTooltip = () => {
    clearTimeout(delayTimeout.current)
    if (delay > 0) {
      delayTimeout.current = setTimeout(setIsOpen, delay * 1000, true)
    } else {
      setIsOpen(true)
    }
  }

  // Small grace period so user can mouse over to tooltip
  const hideTooltip = () => {
    clearTimeout(delayTimeout.current)
    delayTimeout.current = setTimeout(() => setIsOpen(false), 100)
  }

  const cancelHide = () => {
    clearTimeout(delayTimeout.current)
  }

  const triggerProps = {
    ref: refs.setReference,
    onMouseEnter: () => showTooltip(),
    onMouseLeave: () => hideTooltip(),
    onFocus: () => showTooltip(),
    onBlur: () => hideTooltip(),
    'aria-describedby': isOpen ? 'tooltip' : undefined,
  }

  const tooltipStyles = {
    ...floatingStyles,
    zIndex: 'var(--z-index--tooltip)',
  }

  const TriggerIcon = Icon || InfoIcon

  return (
    <div {...triggerProps} className="tooltip--trigger">
      <TriggerIcon className="tooltip--trigger--icon" />
      {isOpen &&
        createPortal(
          <div
            className={`gcds--tooltip tooltip--${currentPosition}${
              width ? ` tooltip--${width}` : ''
            }`}
            ref={refs.setFloating}
            style={tooltipStyles}
            role="tooltip"
            onMouseEnter={cancelHide}
            onMouseLeave={hideTooltip}
          >
            {tooltipContent}
            <div
              ref={arrowRef}
              className="tooltip--arrow"
              style={getArrowStyles(
                {
                  ...(middlewareData.arrow || {}),
                  arrowHeight,
                  arrowWidth,
                },
                currentPosition
              )}
            />
          </div>,
          document.body
        )
      }
    </div>
  )
}
